<template>
  <SectionPageShell
    section="ads"
    :title="adTitle || 'Werbeanzeige-Statistik'"
    :subtitle="adTitle ? 'Statistik' : null"
    show-back-link
  >
    <div v-if="adLink" class="mb-3">
      <a :href="adLink" target="_blank" rel="noopener noreferrer" class="small text-break">
        {{ adLink }}
      </a>
    </div>
    <div class="row">
        <div class="col-12 col-xl-8">
            <div class="card shadow-sm mb-4">
                <div class="card-body">
                    <div class="row g-3 align-items-end">
                        <div class="col-12 col-lg-8">
                            <label for="ad-stats-range" class="form-label">Zeitraum</label>
                            <DaterangePicker id="ad-stats-range" v-model="dateRange" placeholder="Zeitraum auswählen" />
                        </div>
                        <div class="col-12 col-lg-4">
                            <label for="ad-stats-interval" class="form-label">Intervall</label>
                            <select id="ad-stats-interval" v-model="statsStore.interval" class="form-control">
                                <option v-for="option in intervalOptions" :key="option.value" :value="option.value">
                                    {{ option.label }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card shadow-sm mb-4">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <h3 class="h5 mb-0">Klicks im Zeitverlauf</h3>
                    <small class="text-muted">{{ dateLabel }}</small>
                </div>
                <div class="card-body">
                    <div v-if="statsStore.loading" class="text-muted">Lade Statistik...</div>
                    <div v-else-if="!statsData" class="text-muted">Keine Daten verfügbar.</div>
                    <StatsTimeSeriesChart
                        v-else
                        :buckets="histogramBuckets"
                        :interval="statsStore.interval"
                        series-name="Klicks"
                    />
                </div>
            </div>
        </div>
        <div class="col-12 col-xl-4">
            <div class="card shadow-sm mb-4">
                <div class="card-header">
                    <h3 class="h5 mb-0">Überblick</h3>
                </div>
                <div class="card-body">
                    <div class="stat-highlight">
                        <small class="text-uppercase text-muted d-block">Gesamtklicks</small>
                        <strong>{{ totalClicks.toLocaleString() }}</strong>
                    </div>
                    <div class="stat-highlight mt-3">
                        <small class="text-uppercase text-muted d-block">Ø Klicks pro {{ intervalLabel }}</small>
                        <strong>{{ averageClicksPerPeriod.toLocaleString(undefined, {maximumFractionDigits: 1}) }}</strong>
                    </div>
                </div>
            </div>
            <div class="card shadow-sm mb-4">
                <div class="card-header">
                    <h3 class="h5 mb-0">Top Regionen</h3>
                </div>
                <div class="card-body">
                    <DataTableHistogram
                        v-if="regionTableData"
                        :columns="bucketColumns('Region')"
                        :tableData="regionTableData"
                        :enableSearch="false"
                        :enableSize="false"
                        :extraHeader="false"
                    />
                </div>
            </div>
        </div>
    </div>
  </SectionPageShell>
</template>

<script>
import SectionPageShell from "@/components/base_type/SectionPageShell.vue";
import {useAdsStore} from "@/stores/ads";
import {useStatsStore} from "@/stores/stats";
import DaterangePicker from "@/components/DaterangePicker.vue";
import DataTableHistogram from "@/components/DataTableHistogram.vue";
import StatsTimeSeriesChart from "@/components/stats/StatsTimeSeriesChart.vue";
import dayjs from "dayjs";

export default {
    name: "AdStats",
    components: {
        DaterangePicker,
        DataTableHistogram,
        SectionPageShell,
        StatsTimeSeriesChart,
    },
    setup() {
        return {
            adStore: useAdsStore(),
            statsStore: useStatsStore(),
        }
    },
    data() {
        return {
            ad: null,
            statsData: null,
            intervalOptions: [
                {label: 'Tag', value: '1d'},
                {label: 'Woche', value: '1w'},
                {label: 'Monat', value: '1M'},
                {label: 'Quartal', value: '1q'},
                {label: 'Jahr', value: '1y'},
            ],
        }
    },
    created() {
        this.loadAd()
        this.loadStats()
    },
    watch: {
        'statsStore.interval'() {
            this.loadStats()
        }
    },
    computed: {
        dateRange: {
            get() {
                return {
                    from: this.statsStore.from,
                    to: this.statsStore.to,
                }
            },
            set(value) {
                if (!value?.from?.dateInstance || !value?.to?.dateInstance) {
                    return
                }
                this.statsStore.from = dayjs(value.from.dateInstance).valueOf()
                this.statsStore.to = dayjs(value.to.dateInstance).valueOf()
                this.loadStats()
            }
        },
        histogramBuckets() {
            return this.statsData?.aggregations?.histogram?.buckets || []
        },
        adTitle() {
            return this.ad?.title || `Werbeanzeige #${this.$route.params.ad_id}`
        },
        adLink() {
            return this.ad?.link || null
        },
        totalClicks() {
            return this.statsData?.hits?.total?.value || 0
        },
        averageClicksPerPeriod() {
            if (!this.histogramBuckets.length) {
                return 0
            }
            return this.totalClicks / this.histogramBuckets.length
        },
        intervalLabel() {
            const labels = {
                '1d': 'Tag',
                '1w': 'Woche',
                '1M': 'Monat',
                '1q': 'Quartal',
                '1y': 'Jahr',
            }
            return labels[this.statsStore.interval] || 'Zeitraum'
        },
        dateLabel() {
            return `${dayjs(this.statsStore.from).format('DD.MM.YYYY')} bis ${dayjs(this.statsStore.to).format('DD.MM.YYYY')}`
        },
        regionTableData() {
            return this.buildBucketTableData(this.statsData?.aggregations?.regions?.buckets || [])
        },
    },
    methods: {
        loadAd() {
            this.adStore.show(this.$route.params.ad_id).then(({data}) => {
                this.ad = data
            }).finally(() => {
                this.adStore.loading = false
            })
        },
        loadStats() {
            this.statsStore.fetchData('ad_clicks', {
                from: this.statsStore.from,
                to: this.statsStore.to,
                interval: this.statsStore.interval,
                products: this.$route.params.ad_id,
            }).then(({data}) => {
                this.statsData = data
            })
        },
        bucketColumns(label) {
            return [
                {
                    label,
                    field: 'key',
                },
                {
                    label: 'Klicks',
                    field: 'value',
                },
            ]
        },
        buildBucketTableData(buckets) {
            const rows = buckets.map((bucket) => ({
                key: String(bucket.key),
                data: {
                    value: bucket.doc_count,
                }
            }))

            return {
                rows,
                value: {
                    total: rows.reduce((sum, row) => sum + (row.data?.value || 0), 0),
                    compareTotal: 0,
                }
            }
        },
    },
}
</script>

<style scoped lang="scss">
.stat-highlight strong {
    font-size: 2rem;
    line-height: 1;
}
</style>
